import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cable } from '../../cable.js'
import { t } from '../../i18n.js'
import { generateRequestId } from '../utils/requestId.js'
import { buildSavePayload, mergeSavedRowIds } from '../exit_price_assumptions/autosave.js'
import {
  ANALYSIS_END,
  SPECIFIED_DATE,
  MANUAL,
  TWELVE_MONTHS_AFTER_SALE,
  YEAR_OF_SALE,
  PURCHASERS_COSTS,
  VENDORS_COSTS,
  FIXED_AMOUNT,
  PERCENT_OF_NET_SALE_PRICE,
  PERCENT_OF_ADJUSTED_GROSS_SALE_PRICE,
  CAPITAL_DEDUCTION_KEYS,
  blankDraftRow,
  nextLocalRowKey,
  normalizeComputed,
  normalizeProps,
  withSeededRow
} from '../exit_price_assumptions/formState.js'
import {
  adjustByBasisPoints,
  formatDecimalInput,
  formatFixedAmountInput,
  isValidDecimalDraft,
  isValidFixedAmountDraft
} from '../utils/decimalInput.js'

const EXIT_DATE_BASES = [ANALYSIS_END, SPECIFIED_DATE]
const NOI_CALCULATION_METHODS = [TWELVE_MONTHS_AFTER_SALE, YEAR_OF_SALE, MANUAL]
const DEDUCTION_TYPES = [PURCHASERS_COSTS, VENDORS_COSTS]
const INPUT_METHODS = [
  FIXED_AMOUNT, PERCENT_OF_NET_SALE_PRICE, PERCENT_OF_ADJUSTED_GROSS_SALE_PRICE
]

// Order is part of the spec: -25, -5, +5, +25, left to right.
const BASIS_POINT_STEPS = [
  { key: 'minus_25', bps: -25 },
  { key: 'minus_5', bps: -5 },
  { key: 'plus_5', bps: 5 },
  { key: 'plus_25', bps: 25 }
]

// The same debounce the other autosaving tabs use — long enough that a typed
// rate is one request, short enough to feel immediate.
const DEBOUNCE_DELAY = 800

// Caps the set of request ids the cable handler recognises as its own. Far above
// the number of saves that can overlap; it exists only so a broadcast that never
// arrives cannot leak one entry per save.
const MAX_TRACKED_REQUEST_IDS = 20

const isPercentMethod = (method) => method !== FIXED_AMOUNT

function formatAmountDraft(value, inputMethod) {
  return inputMethod === FIXED_AMOUNT ? formatFixedAmountInput(value) : formatDecimalInput(value)
}

// --- display helpers ---------------------------------------------------------
// Everything the server computes arrives as a decimal string or null. Null means
// "cannot be calculated", which is rendered as an en dash rather than a zero — a
// valuer reading 0 where there is no answer would take it for an answer.

const EM_DASH = '–'

function formatWhole(value) {
  if (value === null || value === undefined || value === '') return EM_DASH
  const number = Number(value)
  if (!Number.isFinite(number)) return EM_DASH
  return Math.round(number).toLocaleString('en-GB')
}

// Deductions read as negative money, in parentheses, the way the cash flow does.
// A genuine zero still shows as 0: it is a real, calculated result.
function formatDeduction(value) {
  if (value === null || value === undefined || value === '') return EM_DASH
  const number = Number(value)
  if (!Number.isFinite(number)) return EM_DASH
  if (number === 0) return '0'
  return `(${Math.abs(Math.round(number)).toLocaleString('en-GB')})`
}

function formatPercent(value, places = 2) {
  if (value === null || value === undefined || value === '') return EM_DASH
  const number = Number(value)
  if (!Number.isFinite(number)) return EM_DASH
  return `${number.toFixed(places)}%`
}

function formatYears(value) {
  if (value === null || value === undefined || value === '') return EM_DASH
  const number = Number(value)
  if (!Number.isFinite(number)) return EM_DASH
  return `${number.toFixed(1)} yrs`
}

function formatBps(value) {
  if (value === null || value === undefined) return EM_DASH
  const number = Number(value)
  if (!Number.isFinite(number)) return EM_DASH
  return `${number > 0 ? '+' : ''}${number} bps`
}

// "Jul 2029 – Jun 2030". Used where a rolling twelve-month window does not line
// up with an annual column and so has no "Y11" to be called.
function formatMonthRange(startIso, endIso) {
  if (!startIso || !endIso) return ''
  const month = (iso) => {
    const [year, m] = String(iso).split('-')
    const label = new Date(Number(year), Number(m) - 1, 1)
      .toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    return label
  }
  return `${month(startIso)} \u2013 ${month(endIso)}`
}

function formatIsoDate(value) {
  if (!value) return ''
  const [year, month, day] = String(value).split('-')
  if (!year || !month || !day) return ''
  return `${day}/${month}/${year}`
}

// normalizeProps owns the camelCase/snake_case question (ERB props are
// camelized by the mount controller, fetch and cable payloads are not); this
// only turns the normalized shape into editable draft strings.
function toFormState(source) {
  const normalized = normalizeProps(source)

  return {
    exitRatePercent: formatDecimalInput(normalized.exitRatePercent),
    exitDateBasis: normalized.exitDateBasis,
    specifiedExitDate: normalized.specifiedExitDate || '',
    noiCalculationMethod: normalized.noiCalculationMethod,
    manualNoiToCapitalise: formatFixedAmountInput(normalized.manualNoiToCapitalise),
    addBackVoidDeduction: normalized.addBackVoidDeduction,
    addBackRentFreeDeduction: normalized.addBackRentFreeDeduction,
    capitalDeductions: normalized.capitalDeductions,
    rows: normalized.rows.map((row) => ({
      key: row.id ? `row-${row.id}` : nextLocalRowKey(),
      id: row.id,
      name: row.name,
      deductionType: row.deductionType,
      inputMethod: row.inputMethod,
      amount: row.amount === null ? '' : formatAmountDraft(row.amount, row.inputMethod)
    }))
  }
}

export default function ExitPriceAssumptionsTab(props) {
  const {
    valuationId, updateUrl, stateUrl,
    csrfToken, readOnly = false, translations
  } = props

  const [form, setForm] = useState(() => withSeededRow(toFormState(props), { readOnly }))
  // The computed half is separate state on purpose. It is never edited, and
  // folding it into `form` would make every autosave response look like a change
  // to the user's own inputs — which is exactly what the id-only merge exists to
  // avoid.
  const [computed, setComputed] = useState(() => normalizeComputed(props))
  const [saveError, setSaveError] = useState(null)
  const [rowErrors, setRowErrors] = useState({})
  const [apportionmentOpen, setApportionmentOpen] = useState(false)

  // The form is mirrored in a ref because the debounced flush and the unmount
  // cleanup both read it from outside the render cycle. An effect that synced
  // the ref after commit would let a flush fire against the previous render's
  // form, so every write goes through setFormState and moves both.
  //
  // The rule for reading, since getting it wrong here loses data: rendering and
  // event handlers read `form`, which React has committed by the time they run;
  // anything that runs outside the render cycle — a timer, a cleanup, an unload
  // handler, the continuation after an await — reads `formRef.current`.
  const formRef = useRef(form)
  const dirtyRef = useRef(false)
  const inFlightRef = useRef(false)
  const rerunRef = useRef(false)
  const editSeqRef = useRef(0)
  const flushTimeoutRef = useRef(null)
  const flushRef = useRef(null)
  const ownRequestIdsRef = useRef(new Set())

  const setFormState = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(formRef.current) : updater
    formRef.current = next
    setForm(next)
  }, [])

  const errorMessage = useCallback((code) => {
    // t() returns the lookup path itself on a miss (never falsy), so detect an
    // unmapped error code by comparing against the path and fall back.
    const key = `errors.${code}`
    const message = t(key, translations)
    return message === key ? t('errors.save_failed', translations) : message
  }, [translations])

  // Props are the first paint, the server is the truth. A Turbo restoration
  // visit re-mounts this component from a cached snapshot whose props are as old
  // as the first render of that page — everything autosave has written since is
  // missing from them, and with no Save button the next keystroke would carry
  // that stale form back to the server. The dirty guard is the same one the
  // cable handler uses: never overwrite an unsaved edit.
  useEffect(() => {
    if (!stateUrl) return undefined

    fetch(stateUrl, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((body) => {
        if (!body?.data) return
        // The computed half is always safe to refresh: nothing the user is
        // typing lives in it, and it is the more current of the two.
        setComputed(normalizeComputed(body.data))
        if (dirtyRef.current) return

        setFormState((prev) => withSeededRow(
          toFormState(body.data), { readOnly, previousRows: prev.rows }
        ))
      })
      .catch(() => {})

    return undefined
  }, [stateUrl, readOnly, setFormState])

  // Registers the id so the cable handler can recognise the echo, then sends.
  // `keepalive` is what lets the unload path outlive the document.
  const sendSave = useCallback((current, requestId, keepalive = false) => {
    // FIFO, not clear(): evicting the oldest cannot drop the id of a request
    // still in the air, which is the one thing the set exists to remember.
    if (ownRequestIdsRef.current.size >= MAX_TRACKED_REQUEST_IDS) {
      ownRequestIdsRef.current.delete(ownRequestIdsRef.current.values().next().value)
    }
    ownRequestIdsRef.current.add(requestId)

    return fetch(updateUrl, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify(buildSavePayload(current, requestId)),
      keepalive
    })
  }, [updateUrl, csrfToken])

  // There is no Save button — every edit schedules this. It reads the form from
  // the ref rather than from state so its identity stays stable, which is what
  // keeps the unmount cleanup below firing only on unmount.
  const flushSave = useCallback(async () => {
    if (readOnly || !dirtyRef.current) return
    // A flush requested during a request is queued, never dropped. Dropping it
    // is what a plain `saving` guard would do, and it would silently lose the
    // keystrokes that asked for it.
    if (inFlightRef.current) {
      rerunRef.current = true
      return
    }

    inFlightRef.current = true
    setSaveError(null)

    const seqAtStart = editSeqRef.current
    const current = formRef.current
    const submittedKeys = current.rows.map((row) => row.key)
    const requestId = generateRequestId()

    try {
      const response = await sendSave(current, requestId)
      const body = await response.json()
      if (!body.success) {
        setSaveError({ code: body.error, message: errorMessage(body.error) })
        // The server answers in submitted positions; local state has moved on.
        // Translate through the same keys the id merge uses, or a row removed
        // during the flight shifts every highlight onto the wrong row.
        const errorsByKey = {}
        Object.entries(body.errors_by_index || {}).forEach(([index, rowError]) => {
          const key = submittedKeys[Number(index)]
          if (key) errorsByKey[key] = rowError
        })
        setRowErrors(errorsByKey)
        return
      }

      // The recomputed bridge and analytics are the whole point of the response,
      // so they always land. Only the editable half is protected.
      if (body.data) setComputed(normalizeComputed(body.data))

      // Ids only. exit_price_assumptions/autosave.js has the reasoning: writing
      // the rest back would undo whatever was typed while it was in flight.
      setFormState((prev) => {
        const rows = mergeSavedRowIds(prev.rows, submittedKeys, body.data?.sale_deductions)
        return rows === prev.rows ? prev : { ...prev, rows }
      })
      setRowErrors({})
      // Only clean when nothing was typed during the request. Otherwise the
      // queued flush still has work, and the cable handler must go on treating
      // this form as unsaved so an incoming broadcast cannot clobber it.
      if (editSeqRef.current === seqAtStart) dirtyRef.current = false
    } catch (e) {
      setSaveError({ code: 'save_failed', message: t('errors.save_failed', translations) })
    } finally {
      inFlightRef.current = false
      if (rerunRef.current) {
        rerunRef.current = false
        flushRef.current?.()
      }
    }
  }, [readOnly, sendSave, translations, errorMessage, setFormState])

  useEffect(() => { flushRef.current = flushSave }, [flushSave])

  // Everything below goes through flushRef and takes no dependencies. The point
  // is the cleanup: with `[flushSave]` it re-runs whenever that identity changes,
  // which would silently become "clear the debounce and flush on every render".
  const scheduledSave = useCallback(() => {
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
    flushTimeoutRef.current = setTimeout(() => flushRef.current?.(), DEBOUNCE_DELAY)
  }, [])

  // Leaving the tab is a Turbo navigation, which unmounts the component but
  // leaves the document alive, so a plain fetch started here still lands.
  useEffect(() => () => {
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
    flushRef.current?.()
  }, [])

  // A real unload — reload, tab close, an external link — is the case the
  // cleanup above cannot cover: Stimulus never disconnects, and a plain fetch
  // dies with the document. Since scheduledSave restarts the timer on every
  // keystroke, what is at stake is not 800 ms but everything since the user last
  // paused typing.
  useEffect(() => {
    if (readOnly) return undefined

    const onPageHide = () => {
      if (!dirtyRef.current) return
      sendSave(formRef.current, generateRequestId(), true).catch(() => {})
    }

    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [readOnly, sendSave])

  const touch = useCallback(() => {
    // Unreachable while every input is `disabled` under readOnly, but a change
    // event that slipped through would latch dirtyRef true forever and, as a
    // side effect, mute the cable sync below for the rest of the session.
    if (readOnly) return

    dirtyRef.current = true
    editSeqRef.current += 1
    // Errors are keyed by row index, so any edit that can shift indices makes
    // the highlight point at the wrong row. Clear it rather than mislead.
    setRowErrors({})
    // Only the row-scoped error goes. `sale_deduction_not_found` and its
    // siblings say "reload the page", and that state is terminal.
    setSaveError((prev) => (prev?.code === 'validation_error' ? null : prev))
    scheduledSave()
  }, [readOnly, scheduledSave])

  const setField = useCallback((field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }))
    touch()
  }, [setFormState, touch])

  const toggleCapitalDeduction = useCallback((key, checked) => {
    setFormState((prev) => ({
      ...prev,
      capitalDeductions: { ...prev.capitalDeductions, [key]: checked }
    }))
    touch()
  }, [setFormState, touch])

  const updateRow = useCallback((key, changes) => {
    setFormState((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => (row.key === key ? { ...row, ...changes } : row))
    }))
    touch()
  }, [setFormState, touch])

  const addRow = useCallback(() => {
    setFormState((prev) => ({ ...prev, rows: [...prev.rows, blankDraftRow()] }))
    touch()
  }, [setFormState, touch])

  const removeRow = useCallback((key) => {
    setFormState((prev) => ({ ...prev, rows: prev.rows.filter((row) => row.key !== key) }))
    touch()
  }, [setFormState, touch])

  // Switching method clears the value: the methods hold different kinds of
  // number, so carrying one across would silently reinterpret "5" from 5% into
  // 5 currency units.
  const changeRowMethod = useCallback((key, inputMethod) => {
    updateRow(key, { inputMethod, amount: '' })
  }, [updateRow])

  const applyBasisPoints = useCallback((bps) => {
    const moved = adjustByBasisPoints(form.exitRatePercent, bps)
    if (moved === null) return
    setField('exitRatePercent', moved)
  }, [form.exitRatePercent, setField])

  // A rate is adjustable only once it is a valid number; and an individual step
  // is refused when it would land on zero or below, because nothing can be
  // capitalised at a zero exit rate.
  const canAdjustBy = useCallback((bps) => {
    const moved = adjustByBasisPoints(form.exitRatePercent, bps)
    return moved !== null && Number(moved) > 0
  }, [form.exitRatePercent])

  const manualNoi = form.noiCalculationMethod === MANUAL
  const specifiedDate = form.exitDateBasis === SPECIFIED_DATE

  // Live cross-client sync: skip our own echo, and never clobber unsaved edits.
  useEffect(() => {
    if (!valuationId) return undefined
    const subscription = cable.subscriptions.create(
      { channel: 'ExitPriceAssumptionsChannel', valuation_id: valuationId },
      {
        received: (data) => {
          if (!data) return
          // Our own echo. A set rather than the last id alone: autosave can have
          // two saves in flight, and the earlier echo carries the payload of the
          // earlier save.
          if (ownRequestIdsRef.current.delete(data.request_id)) return
          if (!data.data) return
          setComputed(normalizeComputed(data.data))
          if (dirtyRef.current) return
          // No seed here on purpose — see withSeededRow. An emptied schedule is
          // shown as empty, not answered with a fresh blank row.
          setFormState(toFormState(data.data))
        }
      }
    )
    return () => subscription.unsubscribe()
  }, [valuationId, setFormState])

  const noiMetrics = computed.noiMetrics || {}
  const bridge = computed.bridge || {}
  const analytics = computed.analytics || {}
  const apportionment = computed.rentApportionment || { rows: [], total: null }

  // A window that coincides with an annual column keeps the familiar Y-number,
  // which is what the valuer reads off the Cash Flow tab. A rolling window that
  // straddles two columns has no year, so it is named by its months instead.
  const periodLabel = useMemo(() => (year, start, end, scope) => {
    if (year) return t(`${scope}.selected_year`, translations, { year: `Y${year}` })
    const range = formatMonthRange(start, end)
    if (range) return t(`${scope}.selected_period`, translations, { period: range })
    return t(`${scope}.selected_year_unavailable`, translations)
  }, [translations])

  const noiYearLabel = useMemo(() => {
    if (manualNoi) return t('noi.selected_year_manual', translations)
    return periodLabel(
      noiMetrics.selected_noi_year,
      noiMetrics.selected_noi_period_start,
      noiMetrics.selected_noi_period_end,
      'noi'
    )
  }, [manualNoi, noiMetrics, periodLabel, translations])

  const deductionYearLabel = useMemo(() => periodLabel(
    noiMetrics.selected_deduction_year,
    noiMetrics.selected_deduction_period_start,
    noiMetrics.selected_deduction_period_end,
    'deductions'
  ), [noiMetrics, periodLabel])

  const capitalAmounts = {
    void_deduction: noiMetrics.capital_void_deduction,
    rent_free_deduction: noiMetrics.capital_rent_free_deduction,
    tenant_improvements: noiMetrics.tenant_improvements,
    leasing_commissions: noiMetrics.leasing_commissions,
    capital_expenses: noiMetrics.capital_expenses
  }

  const hasApportionment = Number(apportionment.total || 0) !== 0

  const capitalItemHint = (key) => t('deductions.capital_item_hint', translations, {
    label: t(`deductions.capital_items.${key}`, translations),
    amount: formatWhole(capitalAmounts[key])
  })

  const underOverText = () => {
    const pvm = analytics.passing_vs_market || {}
    if (!pvm.position) return EM_DASH
    if (pvm.position === 'IN_LINE') return t('analytics.passing_vs_market.in_line', translations)
    const key = pvm.position === 'UNDER_RENTED' ? 'under_rented' : 'over_rented'
    return t(`analytics.passing_vs_market.${key}`, translations, {
      amount: formatWhole(Math.abs(Number(pvm.under_over_amount))),
      percent: formatPercent(Math.abs(Number(pvm.under_over_percent)), 1)
    })
  }

  const topTenantText = () => {
    const risk = analytics.lease_risk || {}
    if (!risk.top_tenant_name) return EM_DASH
    return `${risk.top_tenant_name} (${formatPercent(risk.top_tenant_share_percent, 1)})`
  }

  const metricRow = (label, value, options = {}) => (
    <div className={`d-flex justify-content-between ${options.total ? 'exit-analytics__total pt-2 mt-2 border-top fw-bold' : ''}`}>
      <span className={options.total ? '' : 'text-muted small'}>{label}</span>
      <span className="exit-figure fw-semibold text-end">{value}</span>
    </div>
  )

  const bridgeRow = (label, value, options = {}) => (
    <div className={`d-flex justify-content-between py-2 border-bottom ${options.strong ? 'fw-bold' : ''}`}>
      <span>
        {label}
        {options.action}
      </span>
      <span className="exit-figure text-end">{value}</span>
    </div>
  )

  const leaseRisk = analytics.lease_risk || {}
  const passingVsMarket = analytics.passing_vs_market || {}
  const rateSpread = analytics.rate_spread || {}
  const terminal = analytics.terminal_value_reliance || {}

  return (
    <div className="exit-price-assumptions-tab">
      {saveError && <div className="alert alert-danger">{saveError.message}</div>}

      <div className="row">
        <div className="col-12 col-xl-8">
          <div className="row">
            {/* ---- Exit Rate ---- */}
            <div className="col-12 col-md-6 mb-3">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">{t('exit_rate.title', translations)}</h5>
                </div>
                <div className="card-body">
                  <label className="form-label" htmlFor="exit-rate-percent">
                    {t('exit_rate.label', translations)}
                  </label>
                  <input
                    id="exit-rate-percent"
                    type="text"
                    inputMode="decimal"
                    className="form-control"
                    value={form.exitRatePercent}
                    placeholder={t('exit_rate.placeholder', translations)}
                    disabled={readOnly}
                    onChange={(e) => {
                      if (isValidDecimalDraft(e.target.value)) {
                        setField('exitRatePercent', e.target.value)
                      }
                    }}
                    onBlur={(e) => setField('exitRatePercent', formatDecimalInput(e.target.value))}
                  />
                  <div
                    className="btn-group btn-group-sm mt-2 flex-wrap"
                    role="group"
                    aria-label={t('quick_adjust.aria_label', translations)}
                  >
                    {BASIS_POINT_STEPS.map(({ key, bps }) => (
                      <button
                        key={key}
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={readOnly || !canAdjustBy(bps)}
                        onClick={() => applyBasisPoints(bps)}
                      >
                        {t(`quick_adjust.${key}`, translations)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ---- Exit Timing ---- */}
            <div className="col-12 col-md-6 mb-3">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">{t('exit_timing.title', translations)}</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-12 col-sm-6">
                      <label className="form-label" htmlFor="exit-date-basis">
                        {t('exit_timing.basis_label', translations)}
                      </label>
                      <select
                        id="exit-date-basis"
                        className="form-select"
                        value={form.exitDateBasis}
                        disabled={readOnly}
                        onChange={(e) => setField('exitDateBasis', e.target.value)}
                      >
                        {EXIT_DATE_BASES.map((basis) => (
                          <option key={basis} value={basis}>
                            {t(`exit_date_bases.${basis}`, translations)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-sm-6">
                      <label className="form-label" htmlFor="specified-exit-date">
                        {t('exit_timing.date_label', translations)}
                      </label>
                      {/* Under Analysis End the date is derived, so it is shown
                          read-only rather than hidden — the valuer needs to see
                          what the analysis length resolved to. */}
                      {specifiedDate ? (
                        <input
                          id="specified-exit-date"
                          type="date"
                          className="form-control"
                          value={form.specifiedExitDate || ''}
                          disabled={readOnly}
                          onChange={(e) => setField('specifiedExitDate', e.target.value)}
                        />
                      ) : (
                        <input
                          id="specified-exit-date"
                          type="text"
                          className="form-control"
                          value={formatIsoDate(computed.resolvedExitDate)}
                          readOnly
                          disabled
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ---- NOI to Capitalise ---- */}
            <div className="col-12 col-md-6 mb-3">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">{t('noi.title', translations)}</h5>
                </div>
                <div className="card-body">
                  <label className="form-label" htmlFor="noi-calculation-method">
                    {t('noi.method_label', translations)}
                  </label>
                  <select
                    id="noi-calculation-method"
                    className="form-select mb-3"
                    value={form.noiCalculationMethod}
                    disabled={readOnly}
                    onChange={(e) => setField('noiCalculationMethod', e.target.value)}
                  >
                    {NOI_CALCULATION_METHODS.map((method) => (
                      <option key={method} value={method}>
                        {t(`noi_calculation_methods.${method}`, translations)}
                      </option>
                    ))}
                  </select>

                  <label className="form-label" htmlFor="noi-to-capitalise">
                    {t('noi.amount_label', translations)}
                  </label>
                  {/* Only editable under manual NOI; otherwise it displays the
                      figure the cash flow resolved. */}
                  <input
                    id="noi-to-capitalise"
                    type="text"
                    inputMode="decimal"
                    className="form-control"
                    value={manualNoi
                      ? form.manualNoiToCapitalise
                      : formatWhole(noiMetrics.resolved_noi_to_capitalise)}
                    placeholder={t('noi.amount_placeholder', translations)}
                    disabled={readOnly || !manualNoi}
                    readOnly={!manualNoi}
                    onChange={(e) => {
                      if (isValidFixedAmountDraft(e.target.value)) {
                        setField('manualNoiToCapitalise', e.target.value)
                      }
                    }}
                    onBlur={(e) => setField('manualNoiToCapitalise', formatFixedAmountInput(e.target.value))}
                  />
                  <div className="form-text">{noiYearLabel}</div>
                </div>
              </div>
            </div>

            {/* ---- NOI Add-backs ---- */}
            <div className="col-12 col-md-6 mb-3">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">{t('add_backs.title', translations)}</h5>
                </div>
                <div className="card-body">
                  <div className="form-check mb-2">
                    <input
                      id="add-back-void"
                      className="form-check-input"
                      type="checkbox"
                      checked={form.addBackVoidDeduction}
                      disabled={readOnly || manualNoi}
                      onChange={(e) => setField('addBackVoidDeduction', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="add-back-void">
                      {t('add_backs.void', translations)}
                    </label>
                    <div className="form-text">
                      {t('add_backs.void_hint', translations,
                        { amount: formatWhole(noiMetrics.void_add_back) })}
                    </div>
                  </div>
                  <div className="form-check">
                    <input
                      id="add-back-rent-free"
                      className="form-check-input"
                      type="checkbox"
                      checked={form.addBackRentFreeDeduction}
                      disabled={readOnly || manualNoi}
                      onChange={(e) => setField('addBackRentFreeDeduction', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="add-back-rent-free">
                      {t('add_backs.rent_free', translations)}
                    </label>
                    <div className="form-text">
                      {t('add_backs.rent_free_hint', translations,
                        { amount: formatWhole(noiMetrics.rent_free_add_back) })}
                    </div>
                  </div>
                  {manualNoi && (
                    <div className="form-text mt-2">{t('add_backs.manual_hint', translations)}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ---- Sale Price Deductions ---- */}
          <div className="card mb-3">
            <div className="card-header">
              <h5 className="card-title mb-0">{t('deductions.title', translations)}</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-12 col-lg-8 mb-3 mb-lg-0">
                  <h6>{t('deductions.sale_costs_title', translations)}</h6>
                  <p className="text-muted small">
                    {t('deductions.sale_costs_subtitle', translations)}
                  </p>

                  {form.rows.length === 0 ? (
                    <p className="text-muted">{t('deductions.empty', translations)}</p>
                  ) : (
                    <div className="table-responsive">
                      {/* The Input select carries "% of Adjusted Gross Sale
                          Price", which needs real width; below it the wrapper
                          scrolls rather than truncating the option a valuer has
                          to read to know what the number means. */}
                      <table className="table table-sm align-middle" style={{ minWidth: '660px' }}>
                        {/* Explicit widths because the two selects carry the
                            longest labels on the tab — "Purchasers' Costs" and
                            "% of Adjusted Gross Sale Price" — and an even split
                            truncates both to something a valuer cannot tell
                            apart at a glance. The free-text name can afford to
                            be the narrow one. */}
                        <colgroup>
                          <col style={{ width: '17%' }} />
                          <col style={{ width: '22%' }} />
                          <col style={{ width: '34%' }} />
                          <col style={{ width: '19%' }} />
                          <col style={{ width: '8%' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>{t('deductions.columns.name', translations)}</th>
                            <th>{t('deductions.columns.type', translations)}</th>
                            <th>{t('deductions.columns.input', translations)}</th>
                            <th>{t('deductions.columns.amount', translations)}</th>
                            <th className="text-end">{t('deductions.columns.delete', translations)}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.rows.map((row) => (
                            <tr key={row.key} className={rowErrors[row.key] ? 'table-danger' : ''}>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={row.name}
                                  placeholder={t('deductions.name_placeholder', translations)}
                                  disabled={readOnly}
                                  onChange={(e) => updateRow(row.key, { name: e.target.value })}
                                />
                              </td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={row.deductionType}
                                  disabled={readOnly}
                                  aria-label={t('deductions.columns.type', translations)}
                                  onChange={(e) => updateRow(row.key, { deductionType: e.target.value })}
                                >
                                  {DEDUCTION_TYPES.map((type) => (
                                    <option key={type} value={type}>
                                      {t(`deductions.deduction_types.${type}`, translations)}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={row.inputMethod}
                                  disabled={readOnly}
                                  aria-label={t('deductions.columns.input', translations)}
                                  onChange={(e) => changeRowMethod(row.key, e.target.value)}
                                >
                                  {INPUT_METHODS.map((method) => (
                                    <option key={method} value={method}>
                                      {t(`deductions.input_methods.${method}`, translations)}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="form-control form-control-sm"
                                  value={row.amount}
                                  aria-label={t('deductions.columns.amount', translations)}
                                  placeholder={isPercentMethod(row.inputMethod)
                                    ? t('deductions.percent_placeholder', translations)
                                    : t('deductions.amount_placeholder', translations)}
                                  disabled={readOnly}
                                  onChange={(e) => {
                                    const valid = isPercentMethod(row.inputMethod)
                                      ? isValidDecimalDraft(e.target.value)
                                      : isValidFixedAmountDraft(e.target.value)
                                    if (valid) updateRow(row.key, { amount: e.target.value })
                                  }}
                                  onBlur={(e) => updateRow(row.key, {
                                    amount: formatAmountDraft(e.target.value, row.inputMethod)
                                  })}
                                />
                              </td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  disabled={readOnly}
                                  aria-label={t('deductions.delete_row', translations)}
                                  onClick={() => removeRow(row.key)}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {!readOnly && (
                    <button type="button" className="btn btn-sm btn-primary" onClick={addRow}>
                      {t('deductions.add', translations)}
                    </button>
                  )}
                </div>

                <div className="col-12 col-lg-4">
                  <h6>{t('deductions.capital_title', translations)}</h6>
                  <p className="text-muted small mb-1">
                    {t('deductions.capital_subtitle', translations)}
                  </p>
                  <p className="text-muted small">{deductionYearLabel}</p>

                  {CAPITAL_DEDUCTION_KEYS.map((key) => (
                    <div className="form-check mb-2" key={key}>
                      <input
                        id={`capital-deduction-${key}`}
                        className="form-check-input"
                        type="checkbox"
                        checked={Boolean(form.capitalDeductions[key])}
                        disabled={readOnly}
                        onChange={(e) => toggleCapitalDeduction(key, e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor={`capital-deduction-${key}`}>
                        {t(`deductions.capital_items.${key}`, translations)}
                      </label>
                      <div className="form-text">{capitalItemHint(key)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ---- Sale Price Calculation ---- */}
          <div className="card mb-3 sale-price-bridge">
            <div className="card-header">
              <h5 className="card-title mb-0">{t('bridge.title', translations)}</h5>
            </div>
            <div className="card-body">
              {bridgeRow(t('bridge.base_noi', translations), formatWhole(noiMetrics.base_noi_from_cash_flow))}
              {!manualNoi && form.addBackVoidDeduction &&
                bridgeRow(t('bridge.void_add_back', translations), formatWhole(noiMetrics.void_add_back))}
              {!manualNoi && form.addBackRentFreeDeduction &&
                bridgeRow(t('bridge.rent_free_add_back', translations), formatWhole(noiMetrics.rent_free_add_back))}
              {bridgeRow(t('bridge.noi_to_capitalise', translations),
                formatWhole(noiMetrics.resolved_noi_to_capitalise), { strong: true })}
              {bridgeRow(t('bridge.exit_rate', translations), formatPercent(form.exitRatePercent))}
              {bridgeRow(t('bridge.gross_sale_price', translations),
                formatWhole(bridge.gross_sale_price), { strong: true })}

              {(bridge.capital_deduction_lines || []).map((line) => (
                <React.Fragment key={line.key}>
                  {bridgeRow(t(`deductions.capital_items.${line.key}`, translations),
                    formatDeduction(line.amount))}
                </React.Fragment>
              ))}

              {bridgeRow(t('bridge.adjusted_gross_sale_price', translations),
                formatWhole(bridge.adjusted_gross_sale_price), { strong: true })}

              {(bridge.purchaser_cost_lines || []).map((line) => (
                <React.Fragment key={`p-${line.id}`}>
                  {bridgeRow(line.label, formatDeduction(line.amount))}
                </React.Fragment>
              ))}

              {bridgeRow(t('bridge.net_sale_price', translations),
                formatWhole(bridge.net_sale_price), { strong: true })}

              {(bridge.vendor_cost_lines || []).map((line) => (
                <React.Fragment key={`v-${line.id}`}>
                  {bridgeRow(line.label, formatDeduction(line.amount))}
                </React.Fragment>
              ))}

              {hasApportionment && bridgeRow(
                t('bridge.rent_apportionment', translations),
                formatDeduction(bridge.rent_apportionment_at_sale),
                {
                  action: (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 ms-2 align-baseline"
                      onClick={() => setApportionmentOpen(true)}
                    >
                      {t('bridge.view_detail', translations)}
                    </button>
                  )
                }
              )}

              {bridgeRow(t('bridge.proceeds_from_sale', translations),
                formatWhole(bridge.proceeds_from_sale), { strong: true })}
              {bridgeRow(t('bridge.pv_of_proceeds', translations),
                formatWhole(bridge.pv_of_proceeds_from_sale), { strong: true })}
            </div>
          </div>
        </div>

        {/* ---- Exit Analytics ---- */}
        <div className="col-12 col-xl-4">
          <div className="exit-analytics sticky-xl-top" style={{ top: '16px' }}>
            <h5 className="mb-3">{t('analytics.title', translations)}</h5>

            <div className="card mb-3">
              <div className="card-body">
                <h6 className="fw-bold">{t('analytics.lease_risk.title', translations)}</h6>
                {metricRow(t('analytics.lease_risk.wault', translations), formatYears(leaseRisk.wault_years))}
                {metricRow(t('analytics.lease_risk.income_expiring', translations),
                  formatPercent(leaseRisk.income_expiring_12m_percent, 1))}
                {metricRow(t('analytics.lease_risk.top_tenant', translations), topTenantText())}
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body">
                <h6 className="fw-bold">{t('analytics.passing_vs_market.title', translations)}</h6>
                {metricRow(t('analytics.passing_vs_market.passing_rent', translations),
                  formatWhole(passingVsMarket.passing_rent))}
                {metricRow(t('analytics.passing_vs_market.market_rent', translations),
                  formatWhole(passingVsMarket.market_rent))}
                {metricRow(t('analytics.passing_vs_market.under_over', translations), underOverText())}
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body">
                <h6 className="fw-bold">{t('analytics.rate_spread.title', translations)}</h6>
                {metricRow(t('analytics.rate_spread.discount_rate', translations),
                  formatPercent(rateSpread.discount_rate_percent))}
                {metricRow(t('analytics.rate_spread.exit_cap_rate', translations),
                  formatPercent(rateSpread.exit_cap_rate_percent))}
                {metricRow(t('analytics.rate_spread.spread', translations), formatBps(rateSpread.spread_bps))}
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h6 className="fw-bold">{t('analytics.terminal_value.title', translations)}</h6>
                {metricRow(t('analytics.terminal_value.pv_operating', translations),
                  formatWhole(terminal.pv_operating_cash_flow))}
                {metricRow(t('analytics.terminal_value.pv_sale', translations),
                  formatWhole(terminal.pv_sale_proceeds))}
                {/* EVA-296: G = O + T + Q — shown whenever it contributes, or
                    the total below stops adding up. */}
                {Number(terminal.acquisition_apportionment || 0) > 0 &&
                  metricRow(t('analytics.terminal_value.acquisition_apportionment', translations),
                    formatWhole(terminal.acquisition_apportionment))}
                {metricRow(t('analytics.terminal_value.exit_share', translations),
                  formatPercent(terminal.exit_proceeds_share_percent, 1))}
                {metricRow(t('analytics.terminal_value.dcf_gross_value', translations),
                  formatWhole(terminal.dcf_gross_value), { total: true })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {apportionmentOpen && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,.5)' }}
        >
          <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('apportionment_modal.title', translations)}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label={t('apportionment_modal.close', translations)}
                  onClick={() => setApportionmentOpen(false)}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted small">{t('apportionment_modal.subtitle', translations)}</p>
                {(apportionment.rows || []).length === 0 ? (
                  <p className="text-muted">{t('apportionment_modal.empty', translations)}</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>{t('apportionment_modal.columns.tenant', translations)}</th>
                          <th>{t('apportionment_modal.columns.payment_schedule', translations)}</th>
                          <th>{t('apportionment_modal.columns.period_start', translations)}</th>
                          <th>{t('apportionment_modal.columns.period_end', translations)}</th>
                          <th className="text-end">{t('apportionment_modal.columns.period_rent', translations)}</th>
                          <th className="text-end">{t('apportionment_modal.columns.buyer_days', translations)}</th>
                          <th className="text-end">{t('apportionment_modal.columns.total_days', translations)}</th>
                          <th className="text-end">{t('apportionment_modal.columns.apportionment', translations)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apportionment.rows.map((row) => (
                          <tr key={`${row.tenant_id}-${row.period_start}`}>
                            <td>{row.tenant_name}</td>
                            <td>{t(`apportionment_modal.payment_schedules.${row.payment_schedule}`, translations)}</td>
                            <td>{formatIsoDate(row.period_start)}</td>
                            <td>{formatIsoDate(row.period_end)}</td>
                            <td className="text-end font-monospace">{formatWhole(row.period_rent)}</td>
                            <td className="text-end font-monospace">{row.buyer_days}</td>
                            <td className="text-end font-monospace">{row.total_days}</td>
                            <td className="text-end font-monospace">{formatWhole(row.apportionment)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="fw-bold">
                          <td colSpan="7">{t('apportionment_modal.total', translations)}</td>
                          <td className="text-end font-monospace">{formatWhole(apportionment.total)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
